import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Card,
    CardContent,
    Grid,
    Switch,
    FormControlLabel,
    Chip,
    IconButton,
    Tooltip,
    Divider,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Security as SecurityIcon,
    Key as KeyIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';

const VNeIDSettings = () => {
    const { t } = useTranslation();
    const [settings, setSettings] = useState({
        // API Key Management
        unitId: '',
        apiKey: '',
        keyValidityPeriod: '1_year',
        permissionLevel: 'full_access',

        // SDK Version Management
        sdkVersion: 'v2.1.3',
        targetPlatforms: ['dotnet', 'java'],
        packageSize: '24.7',

        // Security Settings
        ipWhitelistRequired: true,
        rateLimiting: true,
        rateLimitValue: 1000,
        rateLimitPeriod: 'hour',
        automaticKeyRotation: true,
        rotationPeriod: 'quarterly',
        logAllRequests: true,

        // Integration Settings
        integrationName: '',
        integrationStatus: 'active',
        dailyTrafficLimit: 5000,
    });

    const handleInputChange = (field, value) => {
        setSettings((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handlePlatformToggle = (platform) => {
        setSettings((prev) => ({
            ...prev,
            targetPlatforms: prev.targetPlatforms.includes(platform)
                ? prev.targetPlatforms.filter((p) => p !== platform)
                : [...prev.targetPlatforms, platform],
        }));
    };

    const generateApiKey = () => {
        const newApiKey = `VNEID-SDK-${settings.unitId.toUpperCase()}-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
        handleInputChange('apiKey', newApiKey);
    };

    const supportedPlatforms = [
        { key: 'dotnet', label: '.NET Framework 4.8+', color: 'primary' },
        { key: 'java', label: 'Java 11+', color: 'success' },
        { key: 'python', label: 'Python 3.8+', color: 'warning' },
        { key: 'nodejs', label: 'NodeJS 16+', color: 'info' },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <KeyIcon color="primary" />
                {t('vneid.settings.title')}
            </Typography>

            <Grid container spacing={3}>
                {/* API Key Management */}
                <Grid item xs={12} md={6}>
                    <Card elevation={2}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <KeyIcon color="primary" />
                                {t('vneid.settings.apiKeyManagement')}
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label={t('vneid.settings.unitId')}
                                    value={settings.unitId}
                                    onChange={(e) => handleInputChange('unitId', e.target.value)}
                                    fullWidth
                                    placeholder={t('vneid.settings.unitIdPlaceholder')}
                                />

                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        label={t('vneid.settings.apiKey')}
                                        value={settings.apiKey}
                                        onChange={(e) => handleInputChange('apiKey', e.target.value)}
                                        fullWidth
                                        placeholder={t('vneid.settings.apiKeyPlaceholder')}
                                        disabled
                                    />
                                    <Button variant="outlined" onClick={generateApiKey} startIcon={<RefreshIcon />} sx={{ minWidth: 'auto', px: 2 }}>
                                        {t('vneid.settings.generate')}
                                    </Button>
                                </Box>

                                <FormControl fullWidth>
                                    <InputLabel>{t('vneid.settings.keyValidityPeriod')}</InputLabel>
                                    <Select
                                        value={settings.keyValidityPeriod}
                                        onChange={(e) => handleInputChange('keyValidityPeriod', e.target.value)}
                                        label={t('vneid.settings.keyValidityPeriod')}
                                    >
                                        <MenuItem value="1_month">{t('vneid.settings.periods.1month')}</MenuItem>
                                        <MenuItem value="3_months">{t('vneid.settings.periods.3months')}</MenuItem>
                                        <MenuItem value="6_months">{t('vneid.settings.periods.6months')}</MenuItem>
                                        <MenuItem value="1_year">{t('vneid.settings.periods.1year')}</MenuItem>
                                        <MenuItem value="2_years">{t('vneid.settings.periods.2years')}</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel>{t('vneid.settings.permissionLevel')}</InputLabel>
                                    <Select
                                        value={settings.permissionLevel}
                                        onChange={(e) => handleInputChange('permissionLevel', e.target.value)}
                                        label={t('vneid.settings.permissionLevel')}
                                    >
                                        <MenuItem value="read_only">{t('vneid.settings.permissions.readOnly')}</MenuItem>
                                        <MenuItem value="limited">{t('vneid.settings.permissions.limited')}</MenuItem>
                                        <MenuItem value="full_access">{t('vneid.settings.permissions.fullAccess')}</MenuItem>
                                        <MenuItem value="admin">{t('vneid.settings.permissions.admin')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* SDK Version Management */}
                <Grid item xs={12} md={6}>
                    <Card elevation={2}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DownloadIcon color="primary" />
                                {t('vneid.settings.sdkVersionManagement')}
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('vneid.settings.sdkVersion')}</InputLabel>
                                    <Select
                                        value={settings.sdkVersion}
                                        onChange={(e) => handleInputChange('sdkVersion', e.target.value)}
                                        label={t('vneid.settings.sdkVersion')}
                                    >
                                        <MenuItem value="v2.1.3">v2.1.3 (Current)</MenuItem>
                                        <MenuItem value="v2.0.8">v2.0.8 (Legacy)</MenuItem>
                                        <MenuItem value="v1.9.2">v1.9.2 (Deprecated)</MenuItem>
                                    </Select>
                                </FormControl>

                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {t('vneid.settings.targetPlatforms')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {supportedPlatforms.map((platform) => (
                                            <Chip
                                                key={platform.key}
                                                label={platform.label}
                                                color={settings.targetPlatforms.includes(platform.key) ? platform.color : 'default'}
                                                variant={settings.targetPlatforms.includes(platform.key) ? 'filled' : 'outlined'}
                                                onClick={() => handlePlatformToggle(platform.key)}
                                                clickable
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                <TextField
                                    label={t('vneid.settings.packageSize')}
                                    value={settings.packageSize}
                                    onChange={(e) => handleInputChange('packageSize', e.target.value)}
                                    fullWidth
                                    disabled
                                    helperText={t('vneid.settings.packageSizeHelper')}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Security Settings */}
                <Grid item xs={12}>
                    <Card elevation={2}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SecurityIcon color="primary" />
                                {t('vneid.settings.securitySettings')}
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.ipWhitelistRequired}
                                                    onChange={(e) => handleInputChange('ipWhitelistRequired', e.target.checked)}
                                                />
                                            }
                                            label={t('vneid.settings.ipWhitelistRequired')}
                                        />

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.rateLimiting}
                                                    onChange={(e) => handleInputChange('rateLimiting', e.target.checked)}
                                                />
                                            }
                                            label={t('vneid.settings.rateLimiting')}
                                        />

                                        {settings.rateLimiting && (
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <TextField
                                                    type="number"
                                                    value={settings.rateLimitValue}
                                                    onChange={(e) => handleInputChange('rateLimitValue', e.target.value)}
                                                    size="small"
                                                    sx={{ width: 120 }}
                                                />
                                                <Typography variant="body2">requests per</Typography>
                                                <Select
                                                    value={settings.rateLimitPeriod}
                                                    onChange={(e) => handleInputChange('rateLimitPeriod', e.target.value)}
                                                    size="small"
                                                    sx={{ minWidth: 100 }}
                                                >
                                                    <MenuItem value="minute">minute</MenuItem>
                                                    <MenuItem value="hour">hour</MenuItem>
                                                    <MenuItem value="day">day</MenuItem>
                                                </Select>
                                            </Box>
                                        )}

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.automaticKeyRotation}
                                                    onChange={(e) => handleInputChange('automaticKeyRotation', e.target.checked)}
                                                />
                                            }
                                            label={t('vneid.settings.automaticKeyRotation')}
                                        />

                                        {settings.automaticKeyRotation && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>{t('vneid.settings.rotationPeriod')}</InputLabel>
                                                <Select
                                                    value={settings.rotationPeriod}
                                                    onChange={(e) => handleInputChange('rotationPeriod', e.target.value)}
                                                    label={t('vneid.settings.rotationPeriod')}
                                                >
                                                    <MenuItem value="monthly">{t('vneid.settings.periods.monthly')}</MenuItem>
                                                    <MenuItem value="quarterly">{t('vneid.settings.periods.quarterly')}</MenuItem>
                                                    <MenuItem value="yearly">{t('vneid.settings.periods.yearly')}</MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.logAllRequests}
                                                    onChange={(e) => handleInputChange('logAllRequests', e.target.checked)}
                                                />
                                            }
                                            label={t('vneid.settings.logAllRequests')}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <TextField
                                            label={t('vneid.settings.integrationName')}
                                            value={settings.integrationName}
                                            onChange={(e) => handleInputChange('integrationName', e.target.value)}
                                            fullWidth
                                            placeholder={t('vneid.settings.integrationNamePlaceholder')}
                                        />

                                        <FormControl fullWidth>
                                            <InputLabel>{t('vneid.settings.integrationStatus')}</InputLabel>
                                            <Select
                                                value={settings.integrationStatus}
                                                onChange={(e) => handleInputChange('integrationStatus', e.target.value)}
                                                label={t('vneid.settings.integrationStatus')}
                                            >
                                                <MenuItem value="active">{t('vneid.settings.status.active')}</MenuItem>
                                                <MenuItem value="inactive">{t('vneid.settings.status.inactive')}</MenuItem>
                                                <MenuItem value="blocked">{t('vneid.settings.status.blocked')}</MenuItem>
                                            </Select>
                                        </FormControl>

                                        <TextField
                                            label={t('vneid.settings.dailyTrafficLimit')}
                                            type="number"
                                            value={settings.dailyTrafficLimit}
                                            onChange={(e) => handleInputChange('dailyTrafficLimit', e.target.value)}
                                            fullWidth
                                            helperText={t('vneid.settings.dailyTrafficLimitHelper')}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default VNeIDSettings;
